/**
 * @fileoverview VAT popover UI reused for Classification Rules, mirroring PaymentVatPopover visuals.
 * @module features/classification-rules/components/RuleVatPopover
 */

import { useState, type ReactElement } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/shared/lib/utils'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import type { ClassificationRuleResponse } from '../schemas/classificationRules'

export interface RuleVatPopoverProps {
  rule: ClassificationRuleResponse
  className?: string
  onApply: (patch: {
    vatApplicable: boolean | null
    reverseCharge: boolean | null
    vatRatePercent: number | null
  }) => Promise<void> | void
}

export default function RuleVatPopover({
  rule,
  className,
  onApply,
}: RuleVatPopoverProps): ReactElement {
  const { t } = useTranslation('features-payments')
  const [open, setOpen] = useState(false)
  const [customMode, setCustomMode] = useState(false)
  const [customRate, setCustomRate] = useState(
    typeof rule.vatRatePercent === 'number' ? rule.vatRatePercent.toFixed(2) : ''
  )
  const [customReverseCharge, setCustomReverseCharge] = useState(Boolean(rule.reverseCharge))
  const [customError, setCustomError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)

  const apply = async (patch: {
    vatApplicable: boolean | null
    reverseCharge: boolean | null
    vatRatePercent: number | null
  }): Promise<void> => {
    try {
      setPending(true)
      await onApply(patch)
      setOpen(false)
      setCustomMode(false)
      toast.success(t('vatRulesPopover.toasts.ruleUpdated'))
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('vatRulesPopover.toasts.updateFailed')
      toast.error(msg)
    } finally {
      setPending(false)
    }
  }

  const handlePreset = async (key: 'standard' | 'reverseCharge' | 'noVat'): Promise<void> => {
    if (key === 'standard') {
      await apply({ vatApplicable: true, reverseCharge: false, vatRatePercent: 23 })
    } else if (key === 'reverseCharge') {
      await apply({ vatApplicable: true, reverseCharge: true, vatRatePercent: 0 })
    } else {
      await apply({ vatApplicable: false, reverseCharge: false, vatRatePercent: null })
    }
  }

  const handleCustomSubmit = async (): Promise<void> => {
    const numeric = Number.parseFloat(customRate.replace(',', '.'))
    if (!Number.isFinite(numeric) || numeric < 0 || numeric > 100) {
      setCustomError(t('ruleVatPopover.customError'))
      return
    }
    setCustomError(null)
    await apply({
      vatApplicable: true,
      reverseCharge: customReverseCharge,
      vatRatePercent: numeric,
    })
  }

  // Label composition based on rule
  let labelText = ''
  if (rule.vatApplicable === null) {
    labelText = t('ruleVatPopover.notSet')
  } else if (rule.vatApplicable === false) {
    labelText = t('vatPopover.labels.noVat')
  } else if (rule.reverseCharge) {
    labelText = '0% VAT'
  } else {
    const pct =
      typeof rule.vatRatePercent === 'number' ? `${rule.vatRatePercent.toFixed(0)}%` : '0%'
    labelText = `${pct} VAT`
  }

  const vatLabel = (
    <span className="inline-flex items-baseline gap-1 text-sm">
      <span>{labelText}</span>
    </span>
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={cn('px-2 justify-start gap-2', className)}
        >
          {vatLabel}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-4 space-y-3" align="start">
        <div className="text-base font-semibold text-foreground">
          {t('ruleVatPopover.title')}
        </div>
        {!customMode ? (
          <div className="space-y-2">
            <Button
              type="button"
              variant="outline"
              className="w-full justify-start gap-2"
              disabled={pending}
              onClick={() => void handlePreset('standard')}
            >
              23% VAT
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full flex-col items-start gap-0.5 h-auto py-2"
              disabled={pending}
              onClick={() => void handlePreset('reverseCharge')}
            >
              <span>0% VAT</span>
              <span className="text-xs text-muted-foreground font-normal">
                ({t('vatPopover.labels.reverseCharge')})
              </span>
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full justify-start gap-2"
              disabled={pending}
              onClick={() => void handlePreset('noVat')}
            >
              {t('vatPopover.labels.noVat')}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full justify-start gap-2"
              disabled={pending}
              onClick={() => setCustomMode(true)}
            >
              {t('vatPopover.preset.custom')}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1">
              <label className="text-sm text-muted-foreground" htmlFor={`rule-${rule.id}-vat-rate`}>
                {t('ruleVatPopover.customRateLabel')}
              </label>
              <Input
                id={`rule-${rule.id}-vat-rate`}
                value={customRate}
                onChange={e => setCustomRate(e.target.value)}
                placeholder={t('ruleVatPopover.customRatePlaceholder')}
              />
              {customError && <div className="text-destructive text-xs">{customError}</div>}
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm">
                {t('vatPopover.custom.reverseChargeLabel')}
              </label>
              <Button
                type="button"
                size="sm"
                variant={customReverseCharge ? 'default' : 'outline'}
                onClick={() => setCustomReverseCharge(v => !v)}
              >
                {customReverseCharge
                  ? t('vatPopover.custom.reverseChargeOn')
                  : t('vatPopover.custom.reverseChargeOff')}
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="default"
                disabled={pending}
                onClick={() => void handleCustomSubmit()}
              >
                {t('vatPopover.custom.apply')}
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={pending}
                onClick={() => setCustomMode(false)}
              >
                {t('vatPopover.custom.cancel')}
              </Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
